// Stock-count list rendered during the missing-item flow.
//
// The count is a REAL stock check of the whole location (a stock_checks row
// with origin='picking_missing'), so this list deliberately mirrors the Stock
// Check module's own screen — same rows, same tick/partial/surplus states,
// same `stock-check-*` classes — so staff get the experience they already
// know. It reads the check's lines (expected vs scanned per SKU) rather than
// the picking-events counts, which is what makes it a full-location count and
// not a target-SKU confirmation.
//
// The originally-missing SKU is pulled out and highlighted as the "target"
// the picker is trying to find, since that's the one that sent them here.
//
// Pure presentational — the parent (ActiveWaveScreen) owns the scan dispatch
// + state machine.
import { MapPin, Check, PackageSearch } from "lucide-react";
import { useTranslation } from "../../i18n";

export interface ExpectedSku {
    line_id: number;
    sku: string;
    qty_required: number;
    qty_picked: number;
    status: string;
}

// One line of the backing stock check.
export interface StockCheckLine {
    sku: string;
    title: string;
    barcode?: string | null;
    expected_qty: number;
    scanned_qty: number;
    /**
     * How much of expected_qty is reserved for an order. These units are on
     * the shelf, so they are counted like anything else — but leaving them
     * unscanned is never a discrepancy, because a picked item is not recorded
     * against inventory until dispatch: it may equally be in a tote already.
     * Greyed to say "accounted for", not "missing".
     */
    allocated_qty?: number;
}

// An item found during the count that's on the picker's own pick list.
export interface PickRelevant {
    sku: string;
    line_id: number;
    location: string;
    qty_outstanding: number;
}

interface Props {
    targetSku: string;          // the originally-missing SKU
    location: string;
    locationDisplayName?: string;
    expected: ExpectedSku[];
    counts: Record<string, number>; // sku -> scanned count this session
    /** Lines of the real stock check. When present this drives the list. */
    stockCheckLines?: StockCheckLine[];
    /** Items found here that the picker still owes on their own wave. */
    pickRelevant?: PickRelevant[];
}

// Cap titles so a long product name doesn't blow out the row — matches the
// Stock Check module's own 40-char cap.
const capTitle = (s: string) => (s && s.length > 40 ? `${s.slice(0, 40)}…` : s);

const MissingItemCountList = ({
    targetSku, location, locationDisplayName, expected, counts,
    stockCheckLines, pickRelevant,
}: Props) => {
    const { t } = useTranslation();

    // Fall back to the legacy expected/counts rendering when no stock check
    // was opened (an older BE bundle, or the check failed to open — the count
    // still works off picking_events, so the picker must not be blocked).
    const useCheck = Array.isArray(stockCheckLines) && stockCheckLines.length > 0;

    const renderCheckRow = (l: StockCheckLine) => {
        const added = l.expected_qty === 0;
        const done = l.scanned_qty > 0 && l.scanned_qty >= l.expected_qty;
        const partial = l.scanned_qty > 0 && l.scanned_qty < l.expected_qty;
        const surplus = l.expected_qty > 0 && l.scanned_qty > l.expected_qty;
        const isTarget = l.sku === targetSku;
        // Wholly reserved and not yet counted — grey it. Once the picker
        // scans it the row behaves normally, so the allocated styling drops
        // away rather than fighting the done/partial states.
        const allocated = Number(l.allocated_qty ?? 0);
        const isAllocated = allocated > 0 && allocated >= l.expected_qty && l.scanned_qty === 0;
        return (
            <div
                key={l.sku}
                className={`stock-check-row${done ? " stock-check-row--done" : ""}`
                    + `${partial ? " stock-check-row--partial" : ""}`
                    + `${surplus ? " stock-check-row--surplus" : ""}`
                    + `${isAllocated ? " stock-check-row--allocated" : ""}`
                    + `${isTarget ? " picking-missing-row--target" : ""}`}
            >
                <div className="stock-check-row-tick">
                    {done
                        ? <Check size={18} />
                        : <span className={`stock-check-row-dot${partial ? " stock-check-row-dot--partial" : ""}`} />}
                </div>
                <div className="stock-check-row-main">
                    <span className="stock-check-row-title">{capTitle(l.title)}</span>
                    <span className="stock-check-row-identity">
                        <span className="stock-check-row-sku">{l.sku}</span>
                        {l.barcode && <span className="stock-check-row-barcode">{l.barcode}</span>}
                        {added && <span className="stock-check-row-added">· {t("stock_check_added")}</span>}
                        {isAllocated && (
                            <span className="stock-check-row-allocated-tag">
                                · {t("stock_check_allocated")}
                            </span>
                        )}
                        {isTarget && (
                            <span className="stock-check-row-added">
                                · {t("picking_missing_section_target")}
                            </span>
                        )}
                    </span>
                </div>
                <div className="stock-check-row-count">
                    {l.scanned_qty}<span className="stock-check-row-count-sep">/</span>{l.expected_qty}
                </div>
            </div>
        );
    };

    // ── Legacy rendering (no backing stock check) ────────────────────
    const target = expected.find(e => e.sku === targetSku);
    const others = expected.filter(e => e.sku !== targetSku);
    const renderLegacyRow = (e: ExpectedSku) => {
        const scanned = counts[e.sku] ?? 0;
        const required = Math.max(0, e.qty_required - e.qty_picked);
        const ok = scanned >= required && required > 0;
        const isTarget = e.sku === targetSku;
        return (
            <li
                key={e.line_id}
                className={`picking-missing-row${isTarget ? " picking-missing-row--target" : ""}${ok ? " picking-missing-row--found" : ""}`}
            >
                <span className="picking-missing-row-sku">{e.sku}</span>
                <span className={`picking-missing-row-count${ok ? " picking-missing-row-count--ok" : ""}`}>
                    {scanned}/{required}
                </span>
            </li>
        );
    };

    const scannedTotal = useCheck
        ? stockCheckLines!.reduce((a, l) => a + Math.min(l.scanned_qty, l.expected_qty), 0)
        : 0;
    const expectedTotal = useCheck
        ? stockCheckLines!.reduce((a, l) => a + l.expected_qty, 0)
        : 0;

    // No outer wrapper — the parent (ActiveWaveScreen) wraps the count list,
    // sub-prompt, and Cancel/Done actions in a single `.picking-missing-screen`
    // so they share the same scrollable container.
    return (
        <>
            <h2 className="picking-missing-title">
                {t("picking_missing_title")}
            </h2>
            {useCheck ? (
                <>
                    {/* Location + running total, mirroring the module's header. */}
                    <div className="stock-check-header">
                        <div className="stock-check-loc">
                            <MapPin size={18} />
                            <strong>{locationDisplayName || location}</strong>
                        </div>
                        <div className="stock-check-counter">
                            <span className="stock-check-counter-num">{scannedTotal}</span>
                            <span className="stock-check-counter-sep">/</span>
                            <span className="stock-check-counter-den">{expectedTotal}</span>
                        </div>
                    </div>
                    <div className="stock-check-list">
                        {stockCheckLines!.map(renderCheckRow)}
                    </div>
                </>
            ) : (
                <>
                    <div className="picking-missing-section-label">
                        {locationDisplayName || location}
                    </div>
                    {target && (
                        <>
                            <div className="picking-missing-section-label">
                                {t("picking_missing_section_target")}
                            </div>
                            <ul className="picking-missing-list">{renderLegacyRow(target)}</ul>
                        </>
                    )}
                    {others.length > 0 && (
                        <>
                            <div className="picking-missing-section-label">
                                {t("picking_missing_section_others")}
                            </div>
                            <ul className="picking-missing-list">{others.map(renderLegacyRow)}</ul>
                        </>
                    )}
                </>
            )}

            {/* Items turned up here that the picker still owes elsewhere on
                their own wave. Persisted for the whole session so it's still
                on screen when they finish counting. */}
            {pickRelevant && pickRelevant.length > 0 && (
                <div className="picking-missing-relevant">
                    <div className="picking-missing-section-label">
                        <PackageSearch size={16} />
                        {" "}{t("picking_missing_relevant_heading")}
                    </div>
                    <ul className="picking-missing-list">
                        {pickRelevant.map(p => (
                            <li key={p.sku} className="picking-missing-row picking-missing-row--found">
                                <span className="picking-missing-row-sku">{p.sku}</span>
                                <span className="picking-missing-row-count">
                                    {t("picking_missing_relevant_at", {
                                        location: p.location,
                                        count: String(p.qty_outstanding),
                                    })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );
};

export default MissingItemCountList;
